// =======================================================
// main.js — entry point
// =======================================================

import { Engine } from './Engine.js';
import { Recorder } from './Recorder.js';
import { UI } from './UI.js';
import { GENERATORS } from './generators/index.js';

// ----- toast helper -----
const toastEl = document.getElementById('toast');
function toast(msg) {
  toastEl.textContent = msg;
  toastEl.classList.add('on');
  clearTimeout(toast._t);
  toast._t = setTimeout(() => toastEl.classList.remove('on'), 1500);
}

// ----- canvas + engine -----
const stage = document.getElementById('stage');
const canvas = document.getElementById('canvas');
const engine = new Engine(canvas);

function fitCanvasToStage() {
  const stageW = Math.max(1, stage.clientWidth);
  const stageH = Math.max(1, stage.clientHeight);
  const renderW = Math.max(1, engine.width);
  const renderH = Math.max(1, engine.height);
  const scale = Math.min(stageW / renderW, stageH / renderH);
  canvas.style.width = `${Math.max(1, Math.floor(renderW * scale))}px`;
  canvas.style.height = `${Math.max(1, Math.floor(renderH * scale))}px`;
}

const _setRenderSize = engine.setRenderSize.bind(engine);
engine.setRenderSize = (width, height) => {
  _setRenderSize(width, height);
  fitCanvasToStage();
};

// Default to the ultrawide LED wall target.
engine.setRenderSize(10900, 540);
window.addEventListener('resize', fitCanvasToStage);

// ----- recorder -----
const recorder = new Recorder(canvas, engine);
const recChip = document.getElementById('rec-chip');
const recTime = document.getElementById('rec-time');
recorder.onStart = () => {
  recChip.style.display = '';
  document.getElementById('run-dot').classList.remove('off');
  document.getElementById('run-lbl').textContent = 'RUNNING';
  toast('Recording started');
  ui.setRecordingIndicator(true);
};
recorder.onStop  = () => {
  recChip.style.display = 'none';
  toast('Capture saved');
  ui.setRecordingIndicator(false);
};
recorder.onExportStart = () => {
  recChip.style.display = '';
  recTime.textContent = 'PNG 00:00';
  document.getElementById('run-dot').classList.remove('off');
  document.getElementById('run-lbl').textContent = 'RUNNING';
  toast('PNG export started');
  ui.setRecordingIndicator(true);
};
recorder.onExportStop = ({ canceled, folderName, framesWritten, totalFrames }) => {
  recChip.style.display = 'none';
  if (canceled) toast(`PNG export stopped (${framesWritten}/${totalFrames})`);
  else toast(`PNG saved to ${folderName}`);
  ui.setRecordingIndicator(false);
};
recorder.onTick  = (s) => {
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(Math.floor(s % 60)).padStart(2, '0');
  recTime.textContent = recorder.isExporting() ? `PNG ${mm}:${ss}` : `REC ${mm}:${ss}`;
};
recorder.onError = (error) => {
  console.error(error);
  toast(error?.message || 'Export failed');
  ui.setRecordingIndicator(false);
};

// ----- generator caching + switching -----
// We keep a single long-lived instance per generator id so their internal
// state (particle positions, etc.) persists across switches. Feels nicer
// than seeding from scratch every time you toggle between two.
const genInstances = {};
function getOrCreate(id) {
  if (!genInstances[id]) {
    const entry = GENERATORS.find(g => g.id === id);
    genInstances[id] = entry.factory();
  }
  return genInstances[id];
}

function switchGenerator(id) {
  const gen = getOrCreate(id);
  engine.setGenerator(gen);
  ui.setGenerator(gen);
  ui.syncGeneratorPicker(id);
  document.getElementById('gen-lbl').textContent = gen.name.toUpperCase();
  toast(gen.name);
}

// ----- UI -----
const ui = new UI({
  mount: document.getElementById('gui-mount'),
  engine,
  recorder,
  generators: GENERATORS,
  onSwitchGenerator: switchGenerator
});

// ----- start on the first generator -----
switchGenerator(GENERATORS[0].id);

// ----- keyboard shortcuts -----
window.addEventListener('keydown', (e) => {
  // don't capture typing into inputs
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
  const k = e.key;

  // Generator shortcut keys.
  {
    const entry = GENERATORS.find(g => String(g.key).toLowerCase() === k.toLowerCase());
    if (entry) switchGenerator(entry.id);
    if (entry) return;
  }
  if (k === ' ') {
    engine.running = !engine.running;
    document.getElementById('run-dot').classList.toggle('off', !engine.running);
    document.getElementById('run-lbl').textContent = engine.running ? 'RUNNING' : 'PAUSED';
    toast(engine.running ? 'Running' : 'Paused');
    e.preventDefault();
    return;
  }
  if (k === 'h' || k === 'H') {
    document.body.classList.toggle('ui-hidden');
    toast(document.body.classList.contains('ui-hidden') ? 'UI hidden (H)' : 'UI visible');
    return;
  }
  if (k === 'f' || k === 'F') {
    if (!document.fullscreenElement) document.body.requestFullscreen();
    else document.exitFullscreen();
    return;
  }
  if (k === 'r' || k === 'R') {
    engine.currentGenerator?.reseed?.();
    toast('Re-seeded');
    return;
  }
  if (k === 'p' || k === 'P') {
    recorder.snapshot();
    toast('Snapshot saved');
    return;
  }
  if (k === 'v' || k === 'V') {
    recorder.toggle();
    return;
  }
  if (k === '[') {
    engine.setPostParam('trails', Math.max(0, engine.post.trails - 0.05));
    toast(`Trails ${engine.post.trails.toFixed(2)}`);
    return;
  }
  if (k === ']') {
    engine.setPostParam('trails', Math.min(0.99, engine.post.trails + 0.05));
    toast(`Trails ${engine.post.trails.toFixed(2)}`);
    return;
  }
});

// ----- status bar -----
const fpsEl = document.getElementById('fps');
const resEl = document.getElementById('res-lbl');
function updateStatus() {
  fpsEl.textContent = `${Math.round(engine.fps)} FPS`;
  const preview = ui.getPreviewSize();
  const output = ui.getOutputSize();
  const clipSec = ui.getClipDuration().toFixed(2);
  resEl.textContent =
    `${preview.width} × ${preview.height} PRE · ${output.width} × ${output.height} OUT · ${clipSec}s`;
}
setInterval(updateStatus, 500);

// ----- render loop -----
function frame() {
  engine.tick();
  requestAnimationFrame(frame);
}
requestAnimationFrame(frame);

// ----- dev convenience: expose for console tinkering -----
window.aiday = { engine, recorder, ui, switchGenerator, GENERATORS, genInstances };
